'use client';
/* External Imports */
import { Link } from '@chakra-ui/next-js';
import { Box, Flex, Text } from '@chakra-ui/react';

/* Built In Imports */
import { useState } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

/* Internal Imports */
/* Components */
import LazyLoadImageComponent from '@app-ps/Components/Ui/LazyLoadImageComponent';
import { refineUrlDomain } from '@app-ps/Components/Utility/utils';
/**
 *
 * @param {Object} pageData
 * @returns
 */
const RelatedPost = ({
  pageData,
  titleText1,
  titleText2,
  region,
  lang,
  ...props
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  // Slider settings
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    afterChange: current => setCurrentSlide(current),

    slidesToShow: 1, // Show only one slide at a time
    slidesToScroll: 1,
    arrows: false,
    responsive: [
      {
        breakpoint: 768, // Below 'md' breakpoint
        settings: {
          slidesToShow: 1, // Show one card at a time on small devices
          slidesToScroll: 1,
          infinite: true,
          dots: true,
        },
      },
    ],
    appendDots: dots => (
      <div
        style={{
          marginTop: '1rem',
          position: 'static',
        }}
      >
        <ul style={{ margin: '0px' }}> {dots} </ul>
      </div>
    ),

    customPaging: function (i) {
      return (
        <div
          style={{
            width: '8px',
            backgroundColor: i === currentSlide ? '#FEFAF6' : '#343638',
            height: '8px',

            borderRadius: '50%',
            display: 'inline-block',
            // transition: 'background-color 0.1s ease',
            margin: '0 -1px',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = '#343638'; // Hover color
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor =
              i === currentSlide ? '#343638' : '#FEFAF6'; // Reset to active or default color
          }}
        ></div>
      );
    },
  };

  return (
    <Box w="100%" pos="relative" bg={'black'} px={{ base: '1rem' }} {...props}>
      {/* box for heading */}
      <Box
        color={'#FEFAF6'}
        fontSize={{ base: '1.5rem', md: '2.25rem' }}
        display={'flex'}
        gap={{ md: '.5rem', base: '.2rem' }}
        alignItems={'center'}
        lineHeight={{ base: '1.8rem', md: '2.25rem' }}
        letterSpacing={'0.16px'}
        justifyContent="center" // Centers items horizontally
      >
        <Text fontWeight={400} fontFamily={'var(--FedraSerifDisplay-Regular)'}>
          {titleText1}
        </Text>
        <Text fontWeight={700} fontFamily={'var(--FedraSerifDisplay-Bold)'}>
          {titleText2}
        </Text>
      </Box>

      {/* Slider for small devices */}
      <Box display={{ base: 'block', md: 'none' }} my="2rem">
        <Slider {...sliderSettings}>
          {pageData?.map((post, index) => (
            <Box
              key={index}
              maxWidth="100%"
              display="flex"
              flexDirection="column"
              gap="1rem"
              cursor="pointer"
            >
              <Link
                href={refineUrlDomain(`blog/${post.urlAlias}`, region, lang)}
                prefetch
              >
                {/* box for image */}
                <Box height="184px">
                  <LazyLoadImageComponent
                    height={'100%'}
                    width={'100%'}
                    objectFit={'cover'}
                    src={post.heroImage.url}
                    alt={post.heroImage.alt || 'Image'}
                  />
                </Box>
                {/* text-1: category */}
                <Text
                  fontWeight={400}
                  color={'#FFFFFF'}
                  mt=".5rem"
                  fontSize="0.875rem"
                  fontFamily="var(--FedraSansStd-book)"
                  lineHeight="1.4rem"
                  letterSpacing="0.32px"
                  opacity="0.8"
                >
                  {post.types.charAt(0).toUpperCase() + post.types.slice(1)} |{' '}
                  {post.category}
                </Text>
                {/* text-2: title */}
                <Text
                  fontWeight={500}
                  fontSize="1rem"
                  fontFamily="var(--FedraSansStd-medium)"
                  lineHeight="1.6rem"
                  letterSpacing="0.32px"
                  color={'#FEFAF6'}
                >
                  {post.title}
                </Text>
                {/* text-3: summary */}
                <Text
                  fontWeight={400}
                  mt=".5rem"
                  color={'#FEFAF6'}
                  fontSize="0.75rem"
                  fontFamily="var(--FedraSansStd-book)"
                  lineHeight="1.2rem"
                  letterSpacing="0.32px"
                  opacity="0.8"
                >
                  {post.summary}
                </Text>
              </Link>
            </Box>
          ))}
        </Slider>
      </Box>

      {/* Flexbox for larger devices */}
      <Flex
        wrap="wrap"
        justifyContent="center"
        mt={{ base: '1rem', md: '2.5rem' }}
        gap={{ base: '1.5rem', md: '2.5rem' }}
        display={{ base: 'none', md: 'flex' }} // Hide flexbox on small devices
      >
        {pageData?.map((post, index) => (
          <Box
            key={index}
            maxWidth={{ lg: '469px' }}
            display="flex"
            flexDirection={'column'}
            gap={{ base: '1rem', md: '2rem', lg: '1.2rem' }}
            cursor="pointer"
          >
            <Link
              href={refineUrlDomain(`blog/${post.urlAlias}`, region, lang)}
              prefetch
            >
              <Box
                height={{ base: '184px', md: '400px', lg: '263px' }}
                width={{ lg: '469px' }}
              >
                <LazyLoadImageComponent
                  height={'100%'}
                  width={'100%'}
                  objectFit={'cover'}
                  src={post.heroImage.url}
                  alt={post.heroImage.alt || 'Image'}
                />
              </Box>
              <Text
                fontWeight={400}
                color={'#FFFFFF'}
                mt={{ md: '2rem', lg: '1rem', base: '.5rem' }}
                fontSize={{ base: '0.875rem' }}
                fontFamily="var(--FedraSansStd-book)"
                lineHeight={{ base: '1.4rem' }}
                letterSpacing={'0.32px'}
                opacity={'0.8'}
              >
                {post.types.charAt(0).toUpperCase() + post.types.slice(1)} |{' '}
                {post.category}
              </Text>
              <Text
                fontWeight={500}
                fontSize={{ base: '1rem', md: '1.25rem' }}
                fontFamily="var(--FedraSansStd-medium)"
                lineHeight={{ base: '1.6rem', md: '2rem' }}
                letterSpacing={'0.32px'}
                color={'#FEFAF6'}
              >
                {post.title}
              </Text>
              <Text
                fontWeight={400}
                mt={{ base: '.5rem' }}
                color={'#FEFAF6'}
                fontSize={{ base: '0.75rem', md: '1rem' }}
                fontFamily="var(--FedraSansStd-book)"
                lineHeight={{ base: '1.2rem', md: '1.6rem' }}
                letterSpacing={'0.32px'}
                opacity={'0.8'}
              >
                {post.summary}
              </Text>
            </Link>
          </Box>
        ))}
      </Flex>
      <style jsx global>
        {`
          .sliderExpressions .slick-dots {
            z-index: 100; /* Ensure dots are visible */
          }
          .sliderExpressions .slick-dots li {
            margin: -20px -20px; /* Adjust the space between dots */
          }
          .sliderExpressions .slick-dots li button::before {
            font-size: 12px; /* Adjust the dot size */
          }
          .sliderExpressions .slick-slide {
          }
          .slick-dots li {
            margin: 0 -2px;
          }
          .sliderExpressions .slick-dots .slick-active > div {
            background-color: red !important;
          }
        `}
      </style>
    </Box>
  );
};

export default RelatedPost;
